import { ResponseTransformed, SearchResponse } from '@empathy/search-adapter';
import {
  container,
  DEPENDENCIES,
  Dictionary,
  logger,
  MUTATIONS_KEYS,
  RootStoreState
} from '@empathybroker/ebx-gluon';
import { Store } from 'vuex';
import { CdlResult, CdlResultPrice } from '../models/result.model';

declare var $nuxt;

const bookFormats = {
  1: 'Tapa Blanda',
  2: 'Bolsillo',
  3: 'Tapa Dura',
  4: 'Tapa blanda firmado',
  5: 'Bolsillo firmado',
  6: 'Tapa dura firmado',
  7: 'Otros formatos',
  8: 'Ebook',
  9: 'Segunda mano',
  10: 'DVD',
  11: 'BLURAY'
};

/**
 * The prices coming from the customer's price service.
 */
export interface Prices {
  /** The type determines if the price is the current value, original value, saving or used. */
  type?: number;
  /** The reference price */
  refAmount?: string;
  /** Currency symbol that should be on the back of the reference price if comes */
  backRefCurrency?: string;
  /** The real price */
  realAmount?: string;
  /** Currency symbol that should be on the front of the real price if comes */
  previousRealCurrency?: string;
  /** Currency symbol that should be on the back of the real price if comes */
  backRealCurrency?: string;
}

const updateResult = (
  result: CdlResult,
  price: Partial<CdlResultPrice>,
  editions: any[],
  infoMessage: string
) => {
  const store = container.get<Store<RootStoreState>>(DEPENDENCIES.Store);
  store.commit(MUTATIONS_KEYS.search.updateResult, {
    result,
    update: { price, editions, infoMessage }
  });
};

const getInfoMessage = (priceInfo: any): string => {
  return priceInfo.mensajeInformativo;
};

const getEditions = (priceInfo: any): any[] => {
  const editions = [];
  if (priceInfo.edicionesAsociadasFichaUnica) {
    priceInfo.edicionesAsociadasFichaUnica.forEach(edition => {
      try {
        const price = edition.listaPrecios[0] || {};
        const type = edition.idTipoFichaUnica;
        const link = edition.link.src || '';
        if (type) {
          editions.push({
            format: bookFormats[type],
            price: parseFloat(price.realAmount),
            link,
            selected: edition.esPrincipal
          });
        }
      } catch (err) {
        logger.error(err);
      }
    });
  }

  return editions;
};

function priceCurrencyFormat(
  value: number,
  backCurrency?: string,
  previousCurrency?: string
): string | null {
  return value
    ? previousCurrency
      ? `${previousCurrency}${value}`
      : `${value} ${backCurrency}`
    : null;
}

export const getResultPrice = (prices: Prices[]): Partial<CdlResultPrice> => {
  const price: Partial<CdlResultPrice> = {};
  if (prices) {
    prices.forEach(
      ({
        realAmount,
        refAmount,
        previousRealCurrency,
        backRealCurrency,
        backRefCurrency,
        type
      }: Prices) => {
        const priceValue = realAmount ? parseFloat(realAmount) : null;
        const refValue = refAmount ? parseFloat(refAmount) : null;
        const latamValue = priceCurrencyFormat(
          priceValue,
          backRealCurrency,
          previousRealCurrency
        );
        const latamRefValue = refValue
          ? priceCurrencyFormat(refValue, backRefCurrency)
          : null;
        switch (type) {
          case 1:
            price.value = priceValue;
            price.latamValue = latamValue;
            price.latamRefValue = latamRefValue;
            break;
          case 2:
            price.originalValue = priceValue;
            price.latamOriginalValue = latamValue;
            price.latamRefOriginalValue = latamRefValue;
            break;
          case 3:
            price.saving = priceValue;
            break;
          case 4:
            price.used = priceValue;
            break;
        }
      }
    );
  }
  return price;
};

const processPricingServiceResponse = (cdlPrices: any, results: {}) => {
  cdlPrices.forEach((priceInfo: any) => {
    const result: CdlResult = results[priceInfo.idProducto];
    if (result) {
      const resultPrice: Partial<CdlResultPrice> = getResultPrice(
        priceInfo.precios
      );
      const editions: any[] = getEditions(priceInfo);
      const infoMessage: string = getInfoMessage(priceInfo);
      updateResult(result, resultPrice, editions, infoMessage);
    }
  });
};

const getPrices = (ids: string[], results: {}) => {
  if (window['$nuxt']) {
    $nuxt.$store.dispatch('search/getPreciosBuscador', ids).then(() => {
      const productsWithPrice = Object.entries(
        $nuxt.$store.state.product.productPrices
      )
        .filter(([id]) => ids.includes(id))
        .map(([_, value]) => value);
      if (productsWithPrice) {
        processPricingServiceResponse(productsWithPrice, results);
      }
    });
  }
};

export const CdlPriceTransformed: ResponseTransformed<any, SearchResponse> = ({
  response
}) => {
  if (response && response.results) {
    const resultsIds: string[] = [];
    const resultsMap = response.results.reduce((map, result: any) => {
      resultsIds.push(result.id);
      map[result.id] = result;
      return map;
    }, {} as Dictionary<CdlResult>);
    getPrices(resultsIds, resultsMap);
  }
};
